import {
  limit,
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  doc,
  deleteDoc,
} from "firebase/firestore";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  Snackbar,
  Pagination,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../config/firebase";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

type Prompt = {
  id: string;
  name: string;
  promptText: string;
  status: number;
  updatedAt: any;
  userId: string;
  isPublic: boolean;
};

export const PromptListPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;
  const [user, setUser] = useState(auth.currentUser);
  const [openDialog, setOpenDialog] = useState(false);
  const location = useLocation();
  // 🔹 認証状態を監視して `user` の情報を取得
  useEffect(() => {

    const shouldRedirect = true; // このページはログイン必須
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      // グローバルなガード（例：未ログインならログインへ）
      if (!currentUser && shouldRedirect) {
        const currentPath = location.pathname + location.search;
        navigate(`/settings/login?redirect=${encodeURIComponent(currentPath)}&reason=auth_required`);
      }
    });

    return () => unsubscribeAuth();
  }, []);


  useEffect(() => {
    if (user === undefined) return; // 初回レンダリング時はスキップ
  
    setLoading(true);
    const promptCollectionRef = collection(db, "prompts");
  
    const userPromptsQuery = user
      ? query(
          promptCollectionRef,
          where("userId", "==", user.uid),
          orderBy("updatedAt", "desc"),
          limit(itemsPerPage)
        )
      : null;
  
    const publicPromptsQuery = query(
      promptCollectionRef,
      where("isPublic", "==", true),
      orderBy("updatedAt", "desc"),
      limit(itemsPerPage)
    );
  
    const userUnsub = userPromptsQuery
      ? onSnapshot(userPromptsQuery, (userSnapshot) => {
          const userPrompts: Prompt[] = userSnapshot.docs.map((doc) => ({
            ...(doc.data() as Omit<Prompt, "id">),
            id: doc.id, // `id` を最後に設定することで上書き
          }));
  
          const publicUnsub = onSnapshot(publicPromptsQuery, (publicSnapshot) => {
            const publicPrompts: Prompt[] = publicSnapshot.docs.map((doc) => ({
              ...(doc.data() as Omit<Prompt, "id">),
              id: doc.id,
            }));
  
            const mergedPrompts: Prompt[] = Array.from(
              new Map([...userPrompts, ...publicPrompts].map((item) => [item.id, item])).values()
            );
  
            setPrompts(mergedPrompts);
            setLoading(false);
          });
  
          return () => publicUnsub();
        })
      : onSnapshot(publicPromptsQuery, (publicSnapshot) => {
          const publicPrompts: Prompt[] = publicSnapshot.docs.map((doc) => ({
            ...(doc.data() as Omit<Prompt, "id">),
            id: doc.id,
          }));
  
          setPrompts(publicPrompts);
          setLoading(false);
        });
  
    return () => userUnsub();
  }, [user, page]);
  
  

  const handleDeletePrompt = async (promptId: string) => {
    try {
      const promptRef = doc(db, "prompts", promptId);
      await deleteDoc(promptRef);
      setMessage("プロンプトを削除しました");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("プロンプトの削除に失敗しました", error);
      setMessage("プロンプトの削除に失敗しました");
      setSnackbarOpen(true);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleCreateClick = () => {
    if (!user) {
      setOpenDialog(true);
    } else {
      navigate("/prompt/new");
    }
  };

  return (
    <Box sx={{ width: "80%", m: "auto", p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5">{t("登録済みのプロンプト")}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
        >
          {t("新規登録")}
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : null}

      {!loading && prompts.length === 0 && (
        <Typography variant="body1" color="textSecondary" align="center">
          表示できるプロンプトがありません。
        </Typography>
      )}

      <List>
        {prompts.map((prompt) => (
          <Box key={prompt.id}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={prompt.name}
                secondary={
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {prompt.promptText.substring(0, 50)}...
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("ステータス")}:
                      {(() => {
                        switch (prompt.status) {
                          case 1:
                            return "オープン";
                          case 2:
                            return "クローズ";
                          case 3:
                            return "削除済み";
                          case 4:
                            return "審査中";
                          default:
                            return "不明";
                        }
                      })()}
                    </Typography>
                  </>
                }
              />
              <IconButton
                edge="end"
                aria-label="edit"
                onClick={() => navigate(`/prompt/edit/${prompt.id}`)}
                disabled={loading}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => handleDeletePrompt(prompt.id)}
                disabled={loading}
              >
                <DeleteIcon />
              </IconButton>
            </ListItem>
            <Divider variant="inset" component="li" />
          </Box>
        ))}
      </List>

      <Pagination
        count={Math.ceil(prompts.length / itemsPerPage)}
        page={page}
        onChange={handlePageChange}
        sx={{ mt: 2 }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={message}
      />

      {/* ポップアップ: 未ログイン時の新規登録誘導 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>会員登録が必要です</DialogTitle>
        <DialogContent>
          <DialogContentText>新規登録するには、まず会員登録を行ってください。</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>キャンセル</Button>
          <Button onClick={() => navigate("/register")} color="primary">
            会員登録へ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
